// 🔐 ROBUST AUTH CONTEXT - OPTIMIZED RBAC LOADING
// =====================================================
// Optimierte AuthContext mit verbesserter RBAC-Performance
// Sequenzielle RBAC-Abfragen für bessere Performance
// =====================================================
import { usePageVisibility } from "../utils/globalErrorHandler.js";
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { masterSupabase } from "../lib/supabase-master.js";
import { usePageVisibilityAuth } from "../hooks/usePageVisibilityAuth.js";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// =====================================================
// 🎯 OPTIMIZED AUTH PROVIDER - FASTER RBAC LOADING
// =====================================================
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [rbacData, setRbacData] = useState(null);
  const [rbacTags, setRbacTags] = useState([]);
  
  // ✅ PERFORMANCE: Cache für RBAC-Daten
  const [rbacCache, setRbacCache] = useState(new Map());
  const [lastAuthCheck, setLastAuthCheck] = useState(null);
  const [isLoadingRBAC, setIsLoadingRBAC] = useState(false); // Guard against duplicate loading
  const [organizationId, setOrganizationId] = useState(null); // Aktive Organization-UUID
  
  // ✅ PERFORMANCE: RBAC Cache für 5 Minuten
  const AUTH_CACHE_DURATION = 5 * 60 * 1000; // 5 Minuten

  // =====================================================
  // 🔄 INITIALIZATION (CLEAN - NO LOOPS)
  // =====================================================
  const initializeAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log("🔄 Initializing authentication...");
      
      const { data: { session }, error } = await masterSupabase.auth.getSession();
      
      if (error) {
        console.error("🚨 Auth initialization error:", error);
        handleUserSignOut();
        return;
      }

      if (session?.user) {
        await handleUserSession(session.user);
      } else {
        console.log("ℹ️ No active session found");
        handleUserSignOut();
      }

      setLastAuthCheck(Date.now());
    } catch (error) {
      console.error("🚨 Auth initialization failed:", error);
      handleUserSignOut();
    } finally {
      setIsLoading(false);
    }
  }, []);

  // =====================================================
  // 🔄 SESSION MANAGEMENT SETUP (CLEAN)
  // =====================================================
  useEffect(() => {
    initializeAuth();

    // ✅ Auth State Change Listener
    const { data: { subscription } } = masterSupabase.auth.onAuthStateChange(
      async (event, session) => {
        try {
          console.log("🔄 Auth State Change Event:", event);
        
        if (event === "TOKEN_REFRESHED") {
          console.log("🔄 Token refreshed - silent background update");
          if (session?.user) {
            // Background refresh: don't show loading screen
            await handleUserSession(session.user, true);
          }
          return;
        }
        
        if (event === "INITIAL_SESSION") {
          return;
        }
        
        switch (event) {
          case "SIGNED_IN":
            console.log("✅ User signed in");
            console.log("🔍 DEBUG: session?.user exists?", !!session?.user);
            console.log("🔍 DEBUG: session data:", session);
            if (session?.user) {
              console.log("🔍 DEBUG: Calling handleUserSession...");
              await handleUserSession(session.user);
              console.log("🔍 DEBUG: handleUserSession completed");
            } else {
              console.error("❌ DEBUG: No user in session!");
            }
            break;
          case "SIGNED_OUT":
            console.log("👋 User signed out");
            handleUserSignOut();
            break;
          default:
            console.log("🔄 Other auth event:", event);
        }
        } catch (error) {
          console.error("🚨 CRITICAL: Auth State Change Handler Error:", error);
          console.error("🚨 Error stack:", error.stack);
        }
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // =====================================================
  // 🔄 CLEAN PAGE VISIBILITY HOOK (SEPARATE)
  // =====================================================
  usePageVisibilityAuth({
    client: masterSupabase,
    isAuthenticated,
    safetyWindowMs: 120000
  });

  // =====================================================
  // 👤 USER SESSION HANDLING
  // =====================================================
  const handleUserSession = async (user, isBackgroundRefresh = false) => {
    // Guard: Prevent duplicate RBAC loading
    if (isLoadingRBAC) {
      console.log("⚠️ RBAC already loading, skipping duplicate call");
      return;
    }
    
    try {
      setIsLoadingRBAC(true);
      if (!isBackgroundRefresh) {
        setIsLoading(true); // Start loading only for initial session, not background refresh
      }
      console.log("👤 Processing user session:", user.email);
            setUser(user);
      setIsAuthenticated(true);
      // Sofort organization_id aus app_metadata setzen (Fallback falls organization_users durch RLS blockiert)
      if (user?.app_metadata?.organization_id) {
        setOrganizationId(user.app_metadata.organization_id);
        console.log('[AuthContext] organizationId pre-set from app_metadata:', user.app_metadata.organization_id);
      }
      // ✅ PERFORMANCE: Cache-Check für RBAC
      const cacheKey = user.id;
      const cachedRBAC = rbacCache.get(cacheKey);
      const now = Date.now();
      
      if (cachedRBAC && (now - cachedRBAC.timestamp) < AUTH_CACHE_DURATION) {
        console.log("✅ Using cached RBAC data");
        setRbacData(cachedRBAC.data);
        setRbacTags(cachedRBAC.tags);
        setIsLoading(false); // End loading when using cache
        return;
      }

      // Lade RBAC-Daten mit optimierter Abfrage
      await loadUserRBACOptimized(user.id, cacheKey);
    } catch (error) {
      console.error("🚨 User session handling error:", error);
    } finally {
      setIsLoading(false); // End loading after RBAC is loaded
      setIsLoadingRBAC(false); // Release guard
    }
  };

  const handleUserSignOut = useCallback(() => {
    console.log("👋 Clearing user session");
    setUser(null);
    setIsAuthenticated(false);
    setRbacData(null);
    setRbacTags([]);
    setRbacCache(new Map());
    setLastAuthCheck(null);
  }, []);

  // =====================================================
  // 🏷️ OPTIMIZED RBAC LOADING - PARALLEL QUERIES
  // =====================================================
  const loadUserRBACOptimized = async (userId, cacheKey, retryCount = 0) => {
    const MAX_RETRIES = 2;
    const TIMEOUT_MS = 10000; // 10 seconds timeout (reduced from 30s)
    
    try {
      console.log(`🏷️ Loading RBAC data for user: ${userId} (attempt ${retryCount + 1})`);
      const startTime = performance.now();
      
      // 🚀 PARALLEL LOADING: Load roles and ALL permissions simultaneously
      // Use organization_users directly (user_roles is blocked by RLS)
      const loadRoles = async () => {
        try {
          console.log('🏷️ Loading roles from organization_users (user_roles is blocked by RLS)...');
          const result = await Promise.race([
            masterSupabase
              .from("organization_users")
              .select("role, organization_id")
              .eq("user_id", userId)
              ,
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error("Organization users timeout")), TIMEOUT_MS)
            )
          ]);
          
          if (result.data && result.data.length > 0) {
            console.log('✅ Successfully loaded roles from organization_users');
            return result;
          }
          
          console.warn('⚠️ No roles found in organization_users');
          return result;
        } catch (error) {
          console.error('❌ Failed to load roles from organization_users:', error);
          throw error;
        }
      };
      
      const [rolesResponse, allPermissionsResponse] = await Promise.all([
        // Query 1: User roles (from organization_users - user_roles is blocked by RLS)
        loadRoles(),
        // Query 2: ALL role permissions (we'll filter client-side)
        Promise.race([
          masterSupabase
            .from("role_permissions")
            .select("*")
            ,
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error("Permissions timeout")), TIMEOUT_MS)
          )
        ])
      ]);
      
      const loadTime = performance.now();
      console.log(`⚡ Parallel RBAC queries completed in: ${Math.round(loadTime - startTime)}ms`);

      // Check for errors
      if (rolesResponse.error) {
        console.error("🚨 Roles loading error:", rolesResponse.error);
        throw rolesResponse.error;
      }
      
      if (allPermissionsResponse.error) {
        console.error("🚨 Permissions loading error:", allPermissionsResponse.error);
        throw allPermissionsResponse.error;
      }

      // Convert response to rolesData format
      // Both user_roles and organization_users return: [{ role: "SUPER_ADMIN" }] (array)
      const roleData = rolesResponse.data || [];
      console.log('[AuthContext] roleData from query:', roleData);
      const rolesData = roleData.length > 0 ? [{ role: roleData[0].role }] : [];
      // Organization-ID aus organization_users (überschreibt app_metadata Fallback wenn vorhanden)
      const orgId = roleData[0]?.organization_id ?? null;
      if (orgId) {
        setOrganizationId(orgId);
        console.log('[AuthContext] organizationId updated from organization_users:', orgId);
      }
      // Falls organization_users leer (RLS), bleibt app_metadata Fallback aus handleUserSession aktiv
      console.log('[AuthContext] processed rolesData:', rolesData);
      
      if (rolesData.length === 0 || !rolesData[0]?.role) {
        console.warn("⚠️ No roles found, using CLIENT fallback");
        const fallbackRBAC = processDirectRBAC([{ role: "CLIENT" }], []);
        setRbacData(fallbackRBAC);
        setRbacTags(fallbackRBAC.tags);
        return;
      }

      // Filter permissions client-side (fast, in-memory operation)
      const roleIds = rolesData.map(r => r.role);
      const permissionsData = (allPermissionsResponse.data || []).filter(p => 
        roleIds.includes(p.role)
      );
      
      console.log(`📋 Loaded ${permissionsData.length} permissions for ${rolesData.length} roles (${roleIds.join(", ")})`);

      // Process RBAC data
      const processedRBAC = processDirectRBAC(rolesData, permissionsData);
      
      // Cache the results (5 minutes TTL)
      setRbacCache(prev => new Map(prev).set(cacheKey, {
        data: processedRBAC,
        tags: processedRBAC.tags,
        timestamp: Date.now()
      }));
      
      setRbacData(processedRBAC);
      setRbacTags(processedRBAC.tags);
      
      const endTime = performance.now();
      console.log("⏱️ RBAC Total time: " + Math.round(endTime - startTime) + "ms");
      console.log("✅ RBAC loaded successfully:", processedRBAC.primaryRole);
      
    } catch (error) {
      console.error(`🚨 RBAC loading failed (attempt ${retryCount + 1}):`, error);
      
      // Retry logic for network issues
      if (retryCount < MAX_RETRIES && (
        error.message.includes("timeout") || 
        error.message.includes("network") ||
        error.message.includes("fetch")
      )) {
        console.log(`🔄 Retrying RBAC loading in 2 seconds... (${retryCount + 1}/${MAX_RETRIES})`);
        setTimeout(() => {
          loadUserRBACOptimized(userId, cacheKey, retryCount + 1);
        }, 2000);
        return;
      }
      
      // Final fallback after all retries - graceful degradation
      console.warn("⚠️ RBAC loading failed after retries, using CLIENT fallback (read-only mode)");
      const fallbackRBAC = processDirectRBAC([{ role: "CLIENT" }], []);
      setRbacData(fallbackRBAC);
      setRbacTags(fallbackRBAC.tags);
    }
  };

  // =====================================================
  // 🏷️ RBAC PROCESSING (UNCHANGED)
  // =====================================================
  const processDirectRBAC = (rolesData, permissionsData) => {
    const roles = rolesData.map(r => ({ name: r.role }));
    const permissions = permissionsData.map(p => ({
      resource: p.resource || "unknown",
      action: p.action || "read",
      scope: "own"
    }));
    const tags = generateRBACTags(rolesData);
    return {
      roles,
      permissions,
      tags,
      primaryRole: rolesData[0]?.role || "CLIENT"
    };
  };

  const generateRBACTags = (rolesData) => {
    const tags = [];
    rolesData.forEach(roleData => {
      // ✅ CLEAN RBAC FIX: Normalize role name (uppercase, trim, replace spaces with underscores)
      const roleName = roleData.role?.toUpperCase().trim().replace(' ', '_');
      
      switch (roleName) {
        case "SUPER_ADMIN":
          tags.push("isSuperAdmin", "hasFullAccess", "canManageAll", "canAccessDebugger");
          break;
        case "ADMIN":
          tags.push("isAdmin", "canManageProperties", "canManageTenants", "canManageUsers");
          break;
        case "MANAGER":
          tags.push("isManager", "canManageAssigned", "hasLimitedAccess");
          break;
        case "CLIENT":
          tags.push("isClient", "canEditOwn", "hasOwnAccess");
          break;
        case "PILOT_CLIENT":
          // 🎯 PILOT_CLIENT: Eingeschränkter Pilot-Mandant — nur 7 Kernbereiche
          tags.push("isPilotClient", "canViewOwn", "hasOwnAccess", "canUseClaraKI");
          break;
        case "VIEWER":
          tags.push("isViewer", "canViewOnly", "hasReadAccess");
          break;
        default:
          // Fallback: If role is unknown, treat as VIEWER (read-only, no access)
          console.warn(`⚠️ Unknown role encountered: ${roleName} (original: ${roleData.role}) — defaulting to VIEWER (read-only)`);
          tags.push("isViewer", "canViewOnly", "hasReadAccess");
      }
    });

    if (tags.includes("isSuperAdmin") || tags.includes("isAdmin")) {
      tags.push("hasFullAccess", "canUseClaraKI", "canManageProperties", "canViewAnalytics");
    } else if (tags.includes("isManager")) {
      tags.push("canUseClaraKI", "canManageProperties");
    } else if (tags.includes("isPilotClient")) {
      // PILOT_CLIENT bekommt canUseClaraKI explizit nur für eigene Org
      tags.push("canUseClaraKI");
    } else if (!tags.includes("isViewer")) {
      tags.push("canUseClaraKI");
    }
    return [...new Set(tags)];
  };

  // =====================================================
  // 🔐 AUTH METHODS (UNCHANGED)
  // =====================================================
  const signIn = useCallback(async (email, password) => {
    try {
      setIsLoading(true);
      const { data, error } = await masterSupabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("🚨 Sign In Error:", error);
      return { data: null, error };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      setIsLoading(true);
      const { error } = await masterSupabase.auth.signOut();
      if (error) throw error;
      handleUserSignOut();
    } catch (error) {
      console.error("🚨 Sign Out Error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [handleUserSignOut]);

  const signUp = useCallback(async (email, password, userData = {}) => {
    try {
      setIsLoading(true);
      console.log("🔄 Starting enhanced registration process...");
      
      // Step 1: Create user in Supabase Auth
      const { data: authData, error: authError } = await masterSupabase.auth.signUp({
        email,
        password,
        options: {
          data: userData
        }
      });
      
      if (authError) {
        console.error("🚨 Auth Sign Up Error:", authError);
        throw authError;
      }
      
      if (!authData.user) {
        throw new Error("User creation failed - no user data returned");
      }
      
      console.log("✅ Auth user created:", authData.user.id);
      
      // Step 2: Create user_profiles entry
      const { error: profileError } = await masterSupabase
        .from("user_profiles")
        .insert({
          id: authData.user.id,
          email: email,
          full_name: userData.full_name || "Neuer Benutzer",
          role: "admin"
        });
      
      if (profileError) {
        console.error("🚨 User Profile Creation Error:", profileError);
        console.warn("⚠️ User profile creation failed, but auth user exists");
      } else {
        console.log("✅ User profile created successfully");
      }
      
      // Step 3: Create user_roles entry
      const { error: roleError } = await masterSupabase
        .from("user_roles")
        .insert({
          user_id: authData.user.id,
          role: "ADMIN",
          assigned_by: authData.user.id,
          is_active: true,
          notes: "Auto-assigned during registration"
        });
      
      if (roleError) {
        console.error("🚨 User Role Creation Error:", roleError);
        console.warn("⚠️ User role creation failed, but auth user exists");
      } else {
        console.log("✅ User role assigned successfully");
      }
      
      console.log("🎉 Enhanced registration completed successfully");
      return { 
        data: authData, 
        error: null,
        success: true,
        profileCreated: !profileError,
        roleAssigned: !roleError
      };
      
    } catch (error) {
      console.error("🚨 Enhanced Sign Up Error:", error);
      return { 
        data: null, 
        error: error,
        success: false,
        profileCreated: false,
        roleAssigned: false
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // =====================================================
  // 🏷️ RBAC HELPER METHODS (UNCHANGED)
  // =====================================================
  const hasTag = useCallback((tag) => {
    return rbacTags.includes(tag);
  }, [rbacTags]);

  const hasRole = useCallback((roleName) => {
    if (!rbacData?.roles) return false;
    // ✅ CLEAN RBAC FIX: Normalize role name for comparison
    const normalizedTarget = roleName?.toUpperCase().trim().replace(' ', '_');
    return rbacData.roles.some(role => role?.name?.toUpperCase().trim().replace(' ', '_') === normalizedTarget);
  }, [rbacData]);

  const hasPermission = useCallback((resource, action, scope = "own") => {
    return (rbacData?.permissions || []).some(perm => 
      perm.resource === resource && 
      perm.action === action && 
      (perm.scope === scope || perm.scope === "all")
    ) || false;
  }, [rbacData]);

  // =====================================================
  // 🔄 MEMOIZED CONTEXT VALUE
  // =====================================================
  const contextValue = useMemo(() => ({
    user,
    isLoading,
    isAuthenticated,
    rbacData,
    rbacTags,
    organizationId,
    signIn,
    signOut,
    signUp,
    hasTag,
    hasRole,
    hasPermission,
    isSuperAdmin: hasTag("isSuperAdmin"),
    isAdmin: hasTag("isAdmin"),
    isManager: hasTag("isManager"),
    isClient: hasTag("isClient"),
    isPilotClient: hasTag("isPilotClient"),
    isViewer: hasTag("isViewer"),
    hasFullAccess: hasTag("hasFullAccess"),
    canUseClaraKI: hasTag("canUseClaraKI"),
    canManageProperties: hasTag("canManageProperties"),
    canAccessDebugger: hasTag("canAccessDebugger")
  }), [
    user, isLoading, isAuthenticated, rbacData, rbacTags, organizationId,
    signIn, signOut, signUp, hasTag, hasRole, hasPermission
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {isLoading ? (
        <div className="fixed inset-0 bg-black flex flex-col items-center justify-center z-50">
          <div className="text-white text-xl font-medium mb-8">Ihre Session wird authentifiziert...</div>
          
          {/* ✅ Clean Loading Spinner */}
          <div className="animate-spin rounded-full h-12 w-12 border-t-3 border-b-3 border-white"></div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
